import { Component } from '@angular/core';

type Platform = 'youtube' | 'twitch' | 'short';
type PlannedWeekOffset = 0 | 1;
type HistoryWeekOffset = -2 | -1 | 0;

interface CalendarEntry {
  weekOffset: number;
  dayIndex: number;
  platform: Platform;
  time: string;
  title: string;
  meta: string;
  status?: string;
}

interface WeekDay {
  index: number;
  date: Date;
  dayName: string;
  dateLabel: string;
  isToday: boolean;
}

@Component({
  selector: 'app-home',
  standalone: true,
  template: `
    <section class="section content-home">
      <div class="page-title-block">
        <h1>CONTENTPLAN</h1>
        <span class="title-line"></span>
      </div>

      <section class="planner-week card accent-card">
        <div class="calendar-toolbar">
          <button class="arrow-button" (click)="previousPlannedWeek()" [disabled]="selectedPlannedWeekOffset === 0" aria-label="Vorherige Woche">‹</button>
          <div class="calendar-title">
            <span>{{ selectedPlannedWeekOffset === 0 ? 'Aktuelle Woche' : 'Nächste Woche' }}</span>
            <strong>{{ selectedPlannedWeekRange }}</strong>
          </div>
          <button class="arrow-button" (click)="nextPlannedWeek()" [disabled]="selectedPlannedWeekOffset === 1" aria-label="Nächste Woche">›</button>
        </div>

        <div class="week-grid calendar-grid">
          @for (day of selectedPlannedWeek; track day.dateLabel) {
            <article class="day-column" [class.today]="day.isToday">
              <div class="day-head">
                <strong>{{ day.dayName }}</strong>
                <span>{{ day.dateLabel }}</span>
              </div>

              <div class="day-entries">
                @for (entry of plannedEntriesFor(day.index); track entry.title) {
                  <div class="plan-entry" [class.youtube]="entry.platform === 'youtube'" [class.twitch]="entry.platform === 'twitch'" [class.short]="entry.platform === 'short'">
                    <div class="platform-icon">{{ iconFor(entry.platform) }}</div>
                    <div>
                      <span class="entry-time">{{ entry.time }}</span>
                      <strong>{{ entry.title }}</strong>
                      <small>{{ entry.meta }}</small>
                    </div>
                  </div>
                } @empty {
                  <p class="empty-day">Noch nichts geplant</p>
                }
              </div>
            </article>
          }
        </div>
      </section>
    </section>

    <section class="section past-section">
      <div class="page-title-block past-title">
        <h1>WAS BISHER GESCHAH</h1>
        <span class="title-line"></span>
      </div>

      <section class="planner-week card accent-card">
        <div class="calendar-toolbar">
          <button class="arrow-button" (click)="previousHistoryWeek()" aria-label="Vorherige Woche">‹</button>
          <div class="calendar-title">
            <span>{{ historyWeekLabel }}</span>
            <strong>{{ selectedHistoryWeekRange }}</strong>
          </div>
          <button class="arrow-button" (click)="nextHistoryWeek()" [disabled]="selectedHistoryWeekOffset === 0" aria-label="Nächste Woche">›</button>
        </div>

        <div class="week-grid calendar-grid history-calendar">
          @for (day of selectedHistoryWeek; track day.dateLabel) {
            <article class="day-column history-day" [class.today]="day.isToday">
              <div class="day-head">
                <strong>{{ day.dayName }}</strong>
                <span>{{ day.dateLabel }}</span>
              </div>

              <div class="day-entries">
                @for (entry of historyEntriesFor(day.index); track entry.title) {
                  <div class="plan-entry history-entry" [class.youtube]="entry.platform === 'youtube'" [class.twitch]="entry.platform === 'twitch'" [class.short]="entry.platform === 'short'">
                    <div class="platform-icon">{{ iconFor(entry.platform) }}</div>
                    <div>
                      <span class="entry-time">{{ entry.time }}</span>
                      <strong>{{ entry.title }}</strong>
                      <small>{{ entry.meta }}</small>
                      @if (entry.status) { <b>{{ entry.status }}</b> }
                    </div>
                  </div>
                } @empty {
                  <p class="empty-day">Keine Einträge</p>
                }
              </div>
            </article>
          }
        </div>
      </section>
    </section>
  `
})
export class HomeComponent {
  private readonly today = new Date();

  selectedPlannedWeekOffset: PlannedWeekOffset = 0;
  selectedHistoryWeekOffset: HistoryWeekOffset = -1;

  readonly contentPlan: CalendarEntry[] = [
    { weekOffset: 0, dayIndex: 0, platform: 'youtube', time: '15:00', title: 'Neue Game-Show Idee erklärt', meta: 'DaniiPlace Hauptkanal' },
    { weekOffset: 0, dayIndex: 1, platform: 'short', time: '12:00', title: 'Best-of Clip: Fast All-in', meta: 'TikTok / Shorts' },
    { weekOffset: 0, dayIndex: 2, platform: 'youtube', time: '15:00', title: 'Quizpoker Konzept & Regeln', meta: 'DaniiPlace Hauptkanal' },
    { weekOffset: 0, dayIndex: 3, platform: 'twitch', time: '17:00–19:00', title: 'Community Teststream', meta: 'Kategorie: Just Chatting' },
    { weekOffset: 0, dayIndex: 4, platform: 'short', time: '18:00', title: 'Stream Moment der Woche', meta: 'TikTok Style Clip' },
    { weekOffset: 0, dayIndex: 6, platform: 'twitch', time: '19:30–22:00', title: 'Show Night Vorbereitung', meta: 'Kategorie: Gaming' },

    { weekOffset: 1, dayIndex: 0, platform: 'youtube', time: '15:00', title: '50 Fragen zu Gaming – Teaser', meta: 'DaniiPlace Hauptkanal' },
    { weekOffset: 1, dayIndex: 2, platform: 'short', time: '12:00', title: 'Quizpoker Regel in 20 Sekunden', meta: 'Shorts / TikTok' },
    { weekOffset: 1, dayIndex: 4, platform: 'twitch', time: '18:00–21:00', title: 'Creator Lobby Test', meta: 'Kategorie: Special Event' },
    { weekOffset: 1, dayIndex: 5, platform: 'youtube', time: '16:00', title: 'Was ist eigentlich DaniiPlace?', meta: 'Brand Video' }
  ];

  readonly historyPlan: CalendarEntry[] = [
    { weekOffset: -2, dayIndex: 2, platform: 'youtube', time: '15:00', title: 'Erster Spielshow-Prototyp', meta: 'DaniiPlace Hauptkanal', status: 'Video online' },
    { weekOffset: -2, dayIndex: 4, platform: 'short', time: '18:30', title: 'Community Clip: knapp daneben', meta: 'TikTok / Shorts', status: 'Short online' },

    { weekOffset: -1, dayIndex: 1, platform: 'twitch', time: '17:00–19:00', title: 'Community Talk & Formatplanung', meta: 'Kategorie: Just Chatting', status: 'Stream beendet' },
    { weekOffset: -1, dayIndex: 2, platform: 'youtube', time: '15:00', title: 'Warum Quizpoker als Show funktionieren kann', meta: 'DaniiPlace Hauptkanal', status: 'Video veröffentlicht' },
    { weekOffset: -1, dayIndex: 4, platform: 'short', time: '12:00', title: 'Der knappste Schätzmoment', meta: 'TikTok / Shorts', status: 'Short online' },

    { weekOffset: 0, dayIndex: 1, platform: 'short', time: '12:00', title: 'Best-of Clip: Fast All-in', meta: 'TikTok / Shorts', status: 'Short online' },
    { weekOffset: 0, dayIndex: 3, platform: 'youtube', time: '15:00', title: 'Creator-Portal Update', meta: 'DaniiPlace Hauptkanal', status: 'Video online' }
  ];

  get selectedPlannedWeek(): WeekDay[] {
    return this.createWeek(this.selectedPlannedWeekOffset);
  }

  get selectedHistoryWeek(): WeekDay[] {
    return this.createWeek(this.selectedHistoryWeekOffset);
  }

  get selectedPlannedWeekRange(): string {
    return this.weekRangeLabel(this.selectedPlannedWeek);
  }

  get selectedHistoryWeekRange(): string {
    return this.weekRangeLabel(this.selectedHistoryWeek);
  }

  get historyWeekLabel(): string {
    if (this.selectedHistoryWeekOffset === 0) return 'Aktuelle Woche';
    if (this.selectedHistoryWeekOffset === -1) return 'Letzte Woche';
    return `${Math.abs(this.selectedHistoryWeekOffset)} Wochen zurück`;
  }

  plannedEntriesFor(dayIndex: number): CalendarEntry[] {
    return this.contentPlan.filter(entry => entry.weekOffset === this.selectedPlannedWeekOffset && entry.dayIndex === dayIndex);
  }

  historyEntriesFor(dayIndex: number): CalendarEntry[] {
    return this.historyPlan.filter(entry => entry.weekOffset === this.selectedHistoryWeekOffset && entry.dayIndex === dayIndex);
  }

  previousPlannedWeek(): void {
    this.selectedPlannedWeekOffset = 0;
  }

  nextPlannedWeek(): void {
    this.selectedPlannedWeekOffset = 1;
  }

  previousHistoryWeek(): void {
    this.selectedHistoryWeekOffset = (this.selectedHistoryWeekOffset - 1) as HistoryWeekOffset;
  }

  nextHistoryWeek(): void {
    if (this.selectedHistoryWeekOffset < 0) {
      this.selectedHistoryWeekOffset = (this.selectedHistoryWeekOffset + 1) as HistoryWeekOffset;
    }
  }

  iconFor(platform: Platform): string {
    if (platform === 'youtube') return '▶';
    if (platform === 'twitch') return '▣';
    return '♫';
  }

  private createWeek(offset: number): WeekDay[] {
    const start = this.startOfWeek(this.today);
    start.setDate(start.getDate() + offset * 7);

    return Array.from({ length: 7 }, (_, index) => {
      const date = new Date(start);
      date.setDate(start.getDate() + index);

      return {
        index,
        date,
        dayName: new Intl.DateTimeFormat('de-AT', { weekday: 'short' }).format(date),
        dateLabel: new Intl.DateTimeFormat('de-AT', { day: '2-digit', month: '2-digit' }).format(date),
        isToday: this.sameDay(date, this.today)
      };
    });
  }

  private startOfWeek(date: Date): Date {
    const result = new Date(date);
    const day = (result.getDay() + 6) % 7;
    result.setDate(result.getDate() - day);
    result.setHours(0, 0, 0, 0);
    return result;
  }

  private sameDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  }

  private weekRangeLabel(days: WeekDay[]): string {
    return `${days[0].dateLabel} – ${days[days.length - 1].dateLabel}`;
  }
}
